import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import {
    MdAdd,
    MdEdit,
    MdDelete,
    MdSave,
    MdClose,
    MdLocalHospital,
    MdSearch,
    MdMoreVert
} from 'react-icons/md';
import { AuthContext } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { API_BASE_URL } from '../lib/constants';

const OTRooms = () => {
    const { userRole, permissions } = useContext(AuthContext);
    const { showNotification } = useNotification();

    const [otRooms, setOtRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Form states
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingRoom, setEditingRoom] = useState(null);
    const [formData, setFormData] = useState({
        room_number: '',
        room_name: '',
        status: 'Active',
        capacity: ''
    });

    const isAuthorized = userRole === 'Admin' || permissions?.includes('OT Rooms Edit') || permissions?.includes('OT Rooms Add');
    const canDelete = userRole === 'Admin' || permissions?.includes('OT Rooms Delete');

    useEffect(() => {
        fetchOtRooms();
    }, []);

    const fetchOtRooms = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/get_ot_rooms`, {
                withCredentials: true
            });
            if (response.data.status === 'success') {
                setOtRooms(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching OT rooms:', error);
            showNotification('error', 'Failed to fetch Operation Theatres');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const openAddForm = () => {
        setFormData({
            room_number: '',
            room_name: '',
            status: 'Active',
            capacity: ''
        });
        setEditingRoom(null);
        setIsFormOpen(true);
    };

    const openEditForm = (room) => {
        setFormData({
            room_number: room.room_number || '',
            room_name: room.room_name || '',
            status: room.status || 'Active',
            capacity: room.capacity || ''
        });
        setEditingRoom(room);
        setIsFormOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            if (editingRoom) {
                // Update
                const response = await axios.put(`${API_BASE_URL}/update_ot_room/${editingRoom.id}`, formData, {
                    withCredentials: true
                });
                if (response.data.status === 'success') {
                    showNotification('success', 'Operation Theatre updated successfully');
                    setIsFormOpen(false);
                    fetchOtRooms();
                }
            } else {
                // Add
                const response = await axios.post(`${API_BASE_URL}/add_ot_room`, formData, {
                    withCredentials: true
                });
                if (response.data.status === 'success') {
                    showNotification('success', 'Operation Theatre added successfully');
                    setIsFormOpen(false);
                    fetchOtRooms();
                }
            }
        } catch (error) {
            console.error('Error saving OT room:', error);
            showNotification('error', error.response?.data?.message || 'Failed to save Operation Theatre details');
        }
    };

    const handleDelete = async (id, roomNumber) => {
        if (window.confirm(`Are you sure you want to delete OT Room - ${roomNumber}?`)) {
            try {
                const response = await axios.delete(`${API_BASE_URL}/delete_ot_room/${id}`, {
                    withCredentials: true
                });
                if (response.data.status === 'success') {
                    showNotification('success', 'Operation Theatre deleted successfully');
                    fetchOtRooms();
                }
            } catch (error) {
                console.error('Error deleting OT room:', error);
                showNotification('error', 'Failed to delete Operation Theatre');
            }
        }
    };

    const filteredRooms = otRooms.filter(room =>
        room.room_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        room.room_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="p-2 sm:p-6 bg-slate-50 min-h-[calc(100vh-100px)]">
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                        <MdLocalHospital className="text-emerald-500" />
                        Operation Theatres
                    </h2>
                    <p className="text-slate-500 text-sm mt-1">Manage hospital operating rooms and capacities</p>
                </div>

                <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-3">
                    <div className="relative">
                        <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Search OT rooms..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full sm:w-64 pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all shadow-sm"
                        />
                    </div>
                    {isAuthorized && (
                        <button
                            onClick={openAddForm}
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-xl transition-all shadow-sm hover:shadow active:scale-[0.98]"
                        >
                            <MdAdd className="w-5 h-5" />
                            Add OT Room
                        </button>
                    )}
                </div>
            </div>

            {/* Content Area */}
            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
                </div>
            ) : filteredRooms.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 text-center border border-slate-200 shadow-sm">
                    <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <MdLocalHospital className="w-8 h-8 text-slate-300" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-700 mb-1">No operating theatres found</h3>
                    <p className="text-slate-500 text-sm mb-6">Start by adding your hospital's operation theatre details.</p>
                    {isAuthorized && (
                        <button
                            onClick={openAddForm}
                            className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 font-medium rounded-xl transition-colors"
                        >
                            <MdAdd className="w-5 h-5" />
                            Add First OT Room
                        </button>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredRooms.map(room => (
                        <div key={room.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow group relative">
                            {/* Action Menu (Visible on hover/focus) */}
                            {isAuthorized && (
                                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                                    <button
                                        onClick={() => openEditForm(room)}
                                        className="p-1.5 bg-white/90 backdrop-blur border border-slate-100 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors shadow-sm"
                                        title="Edit"
                                    >
                                        <MdEdit className="w-4 h-4" />
                                    </button>
                                    {canDelete && (
                                        <button
                                            onClick={() => handleDelete(room.id, room.room_number)}
                                            className="p-1.5 bg-white/90 backdrop-blur border border-slate-100 text-red-600 rounded-lg hover:bg-red-50 transition-colors shadow-sm"
                                            title="Delete"
                                        >
                                            <MdDelete className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            )}

                            <div className="p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center \&#36;{
                                        room.status === 'Active' ? 'bg-emerald-50 text-emerald-600' : 
                                        room.status === 'Maintenance' ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-500'
                                    }`}>
                                        <MdLocalHospital className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-slate-800 text-lg leading-tight">{room.room_number}</h3>
                                        <p className="text-slate-500 text-xs font-medium uppercase tracking-wider mt-0.5">{room.room_name || 'Standard OT'}</p>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-slate-500">Status</span>
                                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold \&#36;{
                                            room.status === 'Active' ? 'bg-emerald-100 text-emerald-700' : 
                                            room.status === 'Maintenance' ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-700'
                                        }`}>
                                            {room.status}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-slate-500">Capacity / Persons</span>
                                        <span className="text-slate-700 font-medium">{room.capacity || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal Form Overlay */}
            {isFormOpen && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                        <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-slate-50/50">
                            <h3 className="text-lg font-bold text-slate-800">
                                {editingRoom ? 'Edit Operation Theatre' : 'Add New Operation Theatre'}
                            </h3>
                            <button
                                onClick={() => setIsFormOpen(false)}
                                className="text-slate-400 hover:text-red-500 transition-colors p-1 rounded-lg hover:bg-slate-100"
                            >
                                <MdClose className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">OT Room Number <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        name="room_number"
                                        required
                                        value={formData.room_number}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-medium"
                                        placeholder="e.g. OT-01, Room A"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Room Type/Name</label>
                                    <input
                                        type="text"
                                        name="room_name"
                                        value={formData.room_name}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-medium"
                                        placeholder="e.g. Cardiac OT, Minor Surgical"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Status</label>
                                        <select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-medium appearance-none"
                                        >
                                            <option value="Active">Active</option>
                                            <option value="Maintenance">Maintenance</option>
                                            <option value="Inactive">Inactive</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Capacity</label>
                                        <input
                                            type="number"
                                            name="capacity"
                                            value={formData.capacity}
                                            onChange={handleInputChange}
                                            min="1"
                                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-medium"
                                            placeholder="e.g. 5"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setIsFormOpen(false)}
                                    className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 hover:bg-slate-50 font-bold rounded-xl transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl transition-colors flex justify-center items-center gap-2 shadow-sm"
                                >
                                    <MdSave className="w-5 h-5" />
                                    {editingRoom ? 'Save Changes' : 'Create Room'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OTRooms;
